import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import {
  mdiLinkVariant,
  mdiAccount,
  mdiDomain,
  mdiCog,
  mdiPackageVariant,
  mdiPlus,
  mdiTrashCanOutline,
  mdiOpenInNew,
  mdiChartLine,
  mdiScaleBalance,
  mdiChevronDown,
  mdiChevronRight
} from '@mdi/js'
import { EmptyState } from '../ui/empty-state'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { $api } from '../../api/queryClient'
import { useApi } from '../../api/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'

interface Props {
  org: string
  documentUid: string
}

interface LinkedEntity {
  uid: string
  name: string
  code?: string | null
  // Owning asset's uid, present for intervention/financial/compliance_evidence
  // so the row can route to that asset's relevant tab.
  asset_uid?: string | null
  // Amount (cents, TTC) + currency, present only for expense targets so the row
  // can render the amount inline (replaces the removed "Related expenses" section).
  amount?: number | null
  currency?: string | null
  // The asset's type, present only for asset targets (rows arrive sorted by it).
  asset_type?: { code: string; name?: Record<string, string>; ordering?: number | null; icon?: string | null } | null
}

interface DocumentLink {
  uid: string
  entity_type: string
  entity_uid: string
  relation_type: string
  created_at: string
  entity?: LinkedEntity | null
}

// Entity types offered in the picker — each maps to a per-type list/search
// endpoint. The API accepts more (organization, job, work_package, financial,
// compliance_evidence) but those are created by other flows, not manually
// linked here. `expense` was removed (spec 2026-07-17-07): expense↔document is
// column-managed via expenses.document_id, mirrored as a `source` link.
const PICKABLE_ENTITY_TYPES = ['asset', 'contract', 'intervention', 'user'] as const
// The manual picker only creates `attachment` links (spec 2026-07-17-07 §6):
// `source` links are exclusively mirror-managed projections of a structural FK
// column (and the delete API rejects them with 409), and `subject` is still
// unwritten — offering either here would let a user create an un-editable link.
const RELATION_TYPES = ['attachment'] as const
// Relation-type render order for the grouped list.
const RELATION_ORDER = ['attachment', 'source'] as const

const ENTITY_ICON: Record<string, string> = {
  asset: SECTION_ICONS.assets,
  contract: SECTION_ICONS.contracts,
  expense: SECTION_ICONS.expenses,
  intervention: SECTION_ICONS.interventions,
  user: mdiAccount,
  organization: mdiDomain,
  job: mdiCog,
  work_package: mdiPackageVariant,
  financial: mdiChartLine,
  compliance_evidence: mdiScaleBalance,
}

// Outer render order for the per-entity-type groups (spec 2026-07-30-03 §1).
// Mirrors ENTITY_ICON's declaration order but states the priority explicitly
// instead of relying on object key order. Any entity_type the API returns that
// is missing here is appended at the end, never dropped.
const ENTITY_TYPE_ORDER = [
  'asset',
  'contract',
  'expense',
  'intervention',
  'user',
  'organization',
  'job',
  'work_package',
  'financial',
  'compliance_evidence',
] as const

// A type carrying at most this many links renders expanded by default —
// collapsing a one- or two-row group is pure friction (spec §4). Applied per
// type, so 18 assets collapse while 2 contracts stay open.
const AUTO_EXPAND_MAX = 3

// Per-type list endpoint + response mapping for the entity picker.
const SEARCH_CONFIG: Record<
  string,
  { path: string; label: (row: any) => string }
> = {
  asset: {
    path: '/api/v1/organizations/{orgCode}/assets',
    label: (a) => (typeof a.name === 'string' ? a.name : a.name?.fr || a.name?.en || a.uid),
  },
  contract: {
    path: '/api/v1/organizations/{orgCode}/contracts',
    label: (c) => c.name || c.code || c.uid,
  },
  intervention: {
    path: '/api/v1/organizations/{orgCode}/interventions',
    label: (i) => i.title || i.reference || i.uid,
  },
  user: {
    path: '/api/v1/organizations/{orgCode}/users',
    label: (u) => u.name || u.email || u.uid,
  },
}

type EntityRoute = { to: string; params: Record<string, string>; search?: Record<string, string> }

// Resolve the in-app route for a linked entity. No linked entity may render as
// dead text (spec 2026-07-17-07 §6): intervention has its own detail page and
// routes there directly via the link's entity_uid; the remaining asset-owned
// types (financial, compliance_evidence) still have no detail page of their
// own, so they route to the parent asset's relevant tab via the hydrated
// entity.asset_uid; the rest to their own detail page or the closest sensible
// landing page.
function entityRouteFor(org: string, link: DocumentLink): EntityRoute | null {
  const entityUid = link.entity_uid
  const assetUid = link.entity?.asset_uid || undefined
  switch (link.entity_type) {
    case 'asset':
      return { to: '/$org/assets/$assetUid', params: { org, assetUid: entityUid } }
    case 'contract':
      return { to: '/$org/contracts/$contractUid', params: { org, contractUid: entityUid } }
    case 'expense':
      return { to: '/$org/expenses/$expenseUid', params: { org, expenseUid: entityUid } }
    case 'user':
      return { to: '/$org/users/$userUid', params: { org, userUid: entityUid } }
    case 'intervention':
      return { to: '/$org/interventions/$interventionUid', params: { org, interventionUid: entityUid } }
    case 'financial':
      return assetUid ? { to: '/$org/assets/$assetUid', params: { org, assetUid } } : null
    case 'compliance_evidence':
      return assetUid ? { to: '/$org/assets/$assetUid', params: { org, assetUid }, search: { tab: 'compliance' } } : null
    case 'work_package':
      return { to: '/$org/capex-plans', params: { org } }
    case 'organization':
      return { to: '/$org/', params: { org } }
    case 'job':
      return { to: '/$org/jobs', params: { org } }
    default:
      return null
  }
}

/**
 * "Linked entities" section on the document detail page. Lists every
 * document_links row (with a hydrated target summary), lets the user attach a
 * new link via an entity-type + entity picker, and remove a link with a
 * confirmation dialog.
 */
export function DocumentLinkedEntities({ org, documentUid }: Props) {
  const { t, i18n } = useTranslation('documents')
  const { request } = useApi()
  const { formatMoney } = useFormatters()
  const queryClient = useQueryClient()

  const [addOpen, setAddOpen] = useState(false)
  const [entityType, setEntityType] = useState<string>('asset')
  const [entityUid, setEntityUid] = useState<string>('')
  const [relationType, setRelationType] = useState<string>('attachment')
  const [formError, setFormError] = useState<string>('')
  const [linkToDelete, setLinkToDelete] = useState<DocumentLink | null>(null)

  const linksQuery = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/documents/{documentUid}/links',
    { params: { path: { orgCode: org, documentUid } } },
    { staleTime: 5 * 60 * 1000, retry: 1, enabled: !!documentUid },
  )

  const links: DocumentLink[] = useMemo(
    () => ((linksQuery.data as any)?.data as DocumentLink[]) || [],
    [linksQuery.data],
  )

  // Two-level grouping (spec 2026-07-30-03 §1): entity_type outer — the axis
  // that actually compresses the list — relation_type inner, still ordered by
  // RELATION_ORDER so `attachment` precedes `source`.
  const groupedByType = useMemo(() => {
    const byType = new Map<string, DocumentLink[]>()
    for (const l of links) {
      const bucket = byType.get(l.entity_type)
      if (bucket) bucket.push(l)
      else byType.set(l.entity_type, [l])
    }
    const known: string[] = ENTITY_TYPE_ORDER.filter((et) => byType.has(et))
    const unknown = [...byType.keys()].filter((et) => !known.includes(et))
    return [...known, ...unknown].map((entityType) => {
      const items = byType.get(entityType) as DocumentLink[]
      const byRelation: Record<string, DocumentLink[]> = {}
      for (const l of items) (byRelation[l.relation_type] ||= []).push(l)
      const knownRel: string[] = RELATION_ORDER.filter((rt) => byRelation[rt]?.length)
      const unknownRel = Object.keys(byRelation).filter((rt) => !knownRel.includes(rt))
      return {
        entityType,
        count: items.length,
        relationGroups: [...knownRel, ...unknownRel].map((rt) => ({
          relationType: rt,
          items: byRelation[rt],
        })),
      }
    })
  }, [links])

  // Types the user has flipped away from their count-derived default. Kept as
  // local view state on purpose (spec §2): a per-section disclosure is
  // ephemeral and does not belong in the document page's search params.
  const [toggledTypes, setToggledTypes] = useState<Set<string>>(new Set())

  const isTypeExpanded = useCallback(
    (entityType: string, count: number) => {
      const byDefault = count <= AUTO_EXPAND_MAX
      return toggledTypes.has(entityType) ? !byDefault : byDefault
    },
    [toggledTypes],
  )

  const toggleType = useCallback((entityType: string) => {
    setToggledTypes((prev) => {
      const next = new Set(prev)
      if (next.has(entityType)) next.delete(entityType)
      else next.add(entityType)
      return next
    })
  }, [])

  // Pluralized per-type count label ("18 assets" / "18 biens"). Built through
  // i18next pluralization, never by concatenating a number with a type label,
  // so FR agreement holds. Unknown types fall back to the generic total key.
  const typeCountLabel = useCallback(
    (entityType: string, count: number) =>
      entityType in ENTITY_ICON
        ? t(`linked_entities.type_summary.${entityType}` as any, { count })
        : t('linked_entities.summary' as any, { count }),
    [t],
  )

  const invalidateLinks = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as any[]
        if (key.length >= 2 && key[0] === 'get') {
          const path = key[1]
          if (path === '/api/v1/organizations/{orgCode}/documents/{documentUid}/links') {
            const params = key.find((i) => i && typeof i === 'object' && 'params' in i)?.params
            return params?.path?.orgCode === org && params?.path?.documentUid === documentUid
          }
        }
        return false
      },
    })
  }, [queryClient, org, documentUid])

  const createMutation = $api.useMutation(
    'post',
    '/api/v1/organizations/{orgCode}/documents/{documentUid}/links',
    {
      onSuccess: () => {
        invalidateLinks()
        setAddOpen(false)
      },
    },
  )

  const deleteMutation = $api.useMutation(
    'delete',
    '/api/v1/organizations/{orgCode}/documents/{documentUid}/links/{linkUid}',
    { onSuccess: () => invalidateLinks() },
  )

  const searchEntities = useCallback(
    async (query: string): Promise<ApiSearchableSelectOption[]> => {
      const cfg = SEARCH_CONFIG[entityType]
      if (!cfg) return []
      try {
        const q: Record<string, string> = { limit: '50' }
        if (query) q.q = query
        const resp = await request(cfg.path, 'GET', {
          params: { path: { orgCode: org }, query: q },
        })
        const rows = (resp as any)?.data || []
        return rows.map((row: any) => ({ value: row.uid, label: cfg.label(row) }))
      } catch (e) {
        console.error('Entity search error:', e)
        return []
      }
    },
    [entityType, org, request],
  )

  const openAdd = () => {
    setEntityType('asset')
    setEntityUid('')
    setRelationType('attachment')
    setFormError('')
    setAddOpen(true)
  }

  const handleConfirm = () => {
    if (!entityUid) {
      setFormError(t('linked_entities.entity_placeholder'))
      return
    }
    setFormError('')
    createMutation.mutate(
      {
        params: { path: { orgCode: org, documentUid } },
        body: { entity_type: entityType, entity_uid: entityUid, relation_type: relationType },
      } as any,
      {
        onError: (err: any) => {
          const msg = String(err?.message || '')
          setFormError(msg.includes('409') || msg.toLowerCase().includes('already')
            ? t('linked_entities.error_duplicate')
            : t('linked_entities.error_create'))
        },
      },
    )
  }

  const handleDeleteConfirm = () => {
    if (!linkToDelete) return
    deleteMutation.mutate(
      { params: { path: { orgCode: org, documentUid, linkUid: linkToDelete.uid } } } as any,
      { onSettled: () => setLinkToDelete(null) },
    )
  }

  if (linksQuery.isLoading) return null

  return (
    <Card className="mb-6" data-testid="document-linked-entities">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <AppIcon path={mdiLinkVariant} size="sm" />
              {t('linked_entities.title')}
            </CardTitle>
            {links.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t(`linked_entities.summary_${links.length === 1 ? 'one' : 'other'}` as any, { count: links.length })}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={openAdd}
            aria-label={t('linked_entities.add')}
            title={t('linked_entities.add')}
            data-testid="document-link-add-btn"
          >
            <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{t('linked_entities.add')}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
      {links.length === 0 ? (
        <EmptyState
          variant="inline"
          icon={mdiLinkVariant}
          title={t('linked_entities.empty')}
          data-testid="document-linked-entities-empty"
        />
      ) : (
        <div className="space-y-2">
          {groupedByType.map(({ entityType, count, relationGroups }) => {
            const expanded = isTypeExpanded(entityType, count)
            const panelId = `document-link-panel-${entityType}`
            const toggleLabel = t(
              expanded ? 'linked_entities.collapse' : 'linked_entities.expand',
            )
            return (
              <div
                key={entityType}
                className="border border-border/50 rounded-md"
                data-testid={`document-link-type-summary-${entityType}`}
              >
                {/* The whole summary row is the toggle: a real <button> so
                    keyboard and screen readers get the disclosure. */}
                <button
                  type="button"
                  onClick={() => toggleType(entityType)}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  aria-label={toggleLabel}
                  title={toggleLabel}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-md hover:bg-muted/50"
                  data-testid={`document-link-type-toggle-${entityType}`}
                >
                  <AppIcon tone="muted" path={ENTITY_ICON[entityType] || mdiLinkVariant} size="sm" className="flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">
                    {typeCountLabel(entityType, count)}
                  </span>
                  <div className="flex-1" />
                  <AppIcon tone="muted" path={expanded ? mdiChevronDown : mdiChevronRight} size="md" className="flex-shrink-0" />
                </button>
                {expanded && (
                  <div id={panelId} className="space-y-3 px-3 pb-2">
                    {relationGroups.map(({ relationType, items }) => {
                      const isSource = relationType === 'source'
                      return (
                        <div key={relationType} className="space-y-1" data-testid={`document-link-group-${relationType}`}>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={isSource ? 'outline' : 'secondary'}
                              className="flex-shrink-0"
                              data-testid={`document-link-group-badge-${relationType}`}
                            >
                              {t(`linked_entities.relation_types.${relationType}` as any, relationType)}
                            </Badge>
                          </div>
                          {items.map((link) => {
                            const route = link.entity ? entityRouteFor(org, link) : null
                            const name = link.entity?.name || t('linked_entities.deleted_target')
                            return (
                    <div
                      key={link.uid}
                      className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 border-border/50"
                      data-testid={`document-link-row-${link.uid}`}
                    >
                      <AppIcon tone="muted" path={ENTITY_ICON[link.entity_type] || mdiLinkVariant} size="sm" className="flex-shrink-0" />
                      <span
                        className="text-xs text-muted-foreground flex-shrink-0"
                        data-testid={`document-link-type-${link.uid}`}
                      >
                        {link.entity_type === 'asset' && link.entity?.asset_type
                          ? link.entity.asset_type.name?.[i18n.language] ||
                            link.entity.asset_type.name?.en ||
                            link.entity.asset_type.name?.fr ||
                            link.entity.asset_type.code
                          : t(`linked_entities.entity_types.${link.entity_type}` as any, link.entity_type)}
                      </span>
                      {route ? (
                        <Link
                          to={route.to}
                          params={route.params as any}
                          search={route.search as any}
                          className="text-sm font-medium text-foreground hover:underline flex-shrink min-w-0 truncate inline-flex items-center gap-1"
                          data-testid={`document-link-target-${link.uid}`}
                        >
                          {name}
                          <AppIcon path={mdiOpenInNew} size="xs" className="opacity-60" />
                        </Link>
                      ) : (
                        <span
                          className={`text-sm flex-shrink min-w-0 truncate ${link.entity ? 'text-foreground' : 'text-muted-foreground italic'}`}
                          data-testid={`document-link-target-${link.uid}`}
                        >
                          {name}
                        </span>
                      )}
                      {link.entity?.code && (
                        <span className="text-xs text-muted-foreground flex-shrink-0">{link.entity.code}</span>
                      )}
                      <div className="flex-1" />
                      {/* Expense links carry their amount (cents, TTC) — the one
                          datum the removed "Related expenses" section provided. */}
                      {link.entity_type === 'expense' && link.entity?.amount != null && (
                        <span
                          className="text-sm font-medium text-foreground flex-shrink-0"
                          data-testid={`document-link-amount-${link.uid}`}
                        >
                          {formatMoney(link.entity.amount, link.entity.currency)}
                        </span>
                      )}
                      {/* Source links are projections of a structural column;
                          deletion is server-rejected (409), so no delete button. */}
                      {!isSource && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive flex-shrink-0"
                          onClick={() => setLinkToDelete(link)}
                          aria-label={t('linked_entities.remove')}
                          title={t('linked_entities.remove')}
                          data-testid={`document-link-remove-${link.uid}`}
                        >
                          <AppIcon path={mdiTrashCanOutline} size="sm" />
                        </Button>
                      )}
                    </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Add-link dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="document-link-dialog">
          <DialogHeader>
            <DialogTitle>{t('linked_entities.add_dialog_title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>{t('linked_entities.entity_type_label')}</Label>
              <Select
                value={entityType}
                onValueChange={(v) => { setEntityType(v); setEntityUid(''); setFormError('') }}
              >
                <SelectTrigger data-testid="document-link-entity-type">
                  <SelectValue placeholder={t('linked_entities.entity_type_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {PICKABLE_ENTITY_TYPES.map((et) => (
                    <SelectItem key={et} value={et} data-testid={`document-link-entity-type-${et}`}>
                      {t(`linked_entities.entity_types.${et}` as any, et)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t('linked_entities.entity_label')}</Label>
              <ApiSearchableSelect
                key={entityType}
                value={entityUid}
                onValueChange={(v) => { setEntityUid(v); setFormError('') }}
                onSearch={searchEntities}
                placeholder={t('linked_entities.entity_placeholder')}
                searchPlaceholder={t('linked_entities.entity_placeholder')}
                data-testid="document-link-entity-picker"
              />
            </div>

            <div className="space-y-1.5">
              <Label>{t('linked_entities.relation_type_label')}</Label>
              <Select value={relationType} onValueChange={setRelationType}>
                <SelectTrigger data-testid="document-link-relation-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATION_TYPES.map((rt) => (
                    <SelectItem key={rt} value={rt} data-testid={`document-link-relation-type-${rt}`}>
                      {t(`linked_entities.relation_types.${rt}` as any, rt)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formError && <p className="text-sm text-destructive" data-testid="document-link-error">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="document-link-cancel">
              {t('linked_entities.cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={createMutation.isPending || !entityUid}
              data-testid="document-link-confirm"
            >
              {t('linked_entities.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={!!linkToDelete} onOpenChange={(o) => !o && setLinkToDelete(null)}>
        <AlertDialogContent data-testid="document-link-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('linked_entities.remove_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('linked_entities.remove_confirm_body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="document-link-remove-cancel">{t('linked_entities.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} data-testid="document-link-remove-confirm">
              {t('linked_entities.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </CardContent>
    </Card>
  )
}
